import { useState } from 'react';
import type { DiscountValidation } from '../../api/types';
import { useValidateDiscount } from '../../api/queries';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatMoney } from '../../lib/format';
import { Button, Input } from '../../ui';

type RejectReason = Extract<DiscountValidation, { valid: false }>['reason'];

/**
 * Svaki razlog odbijanja ima svoju rečenicu. Jedna zajednička poruka („kod ne
 * vrijedi”) natjera korisnika da pogađa je li pogriješio slovo, zakasnio ili
 * kod već iskoristio — a to su tri različite radnje.
 */
const REJECT_KEY: Record<RejectReason, string> = {
  unknown: 'discount.rejectUnknown',
  not_yet: 'discount.rejectNotYet',
  expired: 'discount.rejectExpired',
  used_up: 'discount.rejectUsedUp',
  wrong_product: 'discount.rejectWrongProduct',
  already_used: 'discount.rejectAlreadyUsed',
};

export function rejectMessageKey(reason: RejectReason): string {
  return REJECT_KEY[reason];
}

export interface DiscountCodeFieldProps {
  /** Jedinstven kad na ekranu stoji više paketa — labela i greška vise o istom id-u. */
  id?: string;
  /** Cijena na koju se popust primjenjuje; bez nje server ne zna izračunati iznos. */
  priceId?: string;
  amountCents?: number;
  currency?: string;
  onApplied?: (result: Extract<DiscountValidation, { valid: true }>) => void;
  /** Polje se otvara klikom na „Imaš kod?"; fokus mora prijeći na njega. */
  autoFocus?: boolean;
  className?: string;
}

export function DiscountCodeField({
  id = 'discount-code',
  priceId,
  amountCents,
  currency = 'EUR',
  onApplied,
  autoFocus = false,
  className,
}: DiscountCodeFieldProps) {
  const { t, locale } = useTranslation();
  const validate = useValidateDiscount();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<DiscountValidation | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function check() {
    const trimmed = code.trim();
    if (!trimmed) return;
    setResult(null);
    setFailure(null);
    try {
      const validation = await validate.mutateAsync({
        code: trimmed,
        ...(priceId ? { priceId } : {}),
      });
      setResult(validation);
      if (validation.valid) onApplied?.(validation);
    } catch (err) {
      // Pad mreže nije odbijen kod — poruka mora reći da provjera nije ni prošla.
      setFailure(toErrorMessage(t, err).title);
    }
  }

  const rejected = result && !result.valid ? result : null;
  const accepted = result?.valid ? result : null;

  const offCents =
    accepted?.amountOffCents ??
    (accepted && amountCents !== undefined
      ? accepted.kind === 'percent'
        ? Math.round((amountCents * accepted.value) / 100)
        : Math.min(accepted.value, amountCents)
      : null);

  const error = rejected ? t(rejectMessageKey(rejected.reason)) : (failure ?? undefined);

  return (
    <div className={className} data-testid="discount-field">
      <div className="flex flex-wrap items-start gap-3">
        <Input
          id={id}
          label={t('discount.label')}
          placeholder={t('discount.placeholder')}
          value={code}
          autoComplete="off"
          autoFocus={autoFocus}
          onChange={(e) => {
            setCode(e.target.value);
            setResult(null);
            setFailure(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void check();
            }
          }}
          error={error}
          className="min-w-[12rem]"
        />
        <Button
          variant="secondary"
          className="mt-[26px]"
          loading={validate.isPending}
          disabled={code.trim().length === 0}
          onClick={() => void check()}
        >
          {t('discount.apply')}
        </Button>
      </div>

      {accepted && (
        <p
          data-testid="discount-applied"
          className="mt-3 rounded-md bg-success/10 px-4 py-3 text-[13.5px] leading-relaxed text-success-dark"
        >
          {offCents !== null && offCents > 0
            ? t('discount.appliedAmount', {
                code: accepted.code,
                amount: formatMoney(offCents, currency, locale),
                ...(amountCents !== undefined
                  ? { total: formatMoney(Math.max(0, amountCents - offCents), currency, locale) }
                  : { total: '' }),
              })
            : accepted.kind === 'percent'
              ? t('discount.appliedPercent', { code: accepted.code, value: accepted.value })
              : t('discount.appliedPlain', { code: accepted.code })}
        </p>
      )}
    </div>
  );
}
